import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { finalize, map, mergeMap, tap } from 'rxjs';

import {
  ButtonIconAtomComponent,
  CardAtomComponent,
  CheckboxAtomComponent,
  InputAtomComponent,
  ProgressSpinnerAtomComponent,
  ToastAtomComponent,
} from '@ls-commons/components';
import { ModalService } from '@ls-commons/utils/modals';
import { Language, Section, SectionType } from '@lemonsolutions/models';
import { UtilsService } from '@ls-commons/utils';
import { FlagLanguagePipe, InfoLanguagePipe } from '@ls-commons/utils/pipes';

import { Table, TableModule, TableRowReorderEvent } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';

import { SectionEditComponent } from '../section-edit/section-edit.component';
import { SectionService } from '../section.service';
interface Column {
  field: string;
  header: string;
  customExportHeader?: string;
}

@Component({
  selector: 'lib-sections',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    DialogModule,
    ConfirmDialogModule,
    FormsModule,
    TagModule,
    DropdownModule,
    InputAtomComponent,
    ToastAtomComponent,
    ButtonIconAtomComponent,
    CardAtomComponent,
    FlagLanguagePipe,
    ProgressSpinnerAtomComponent,
    CheckboxAtomComponent,
    InfoLanguagePipe,
  ],
  providers: [ModalService, DialogService, ConfirmationService],
  templateUrl: './section-list.component.html',
  styleUrl: './section-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SectionListComponent implements OnInit {
  @Input() title = '';
  @Input() type = 0;

  @ViewChild('dt') dt!: Table;

  languages!: Language[];
  selectedSectionType?: SectionType;
  selectedCountry!: Language;
  sectionService = inject(SectionService);
  route = inject(ActivatedRoute);
  modalService = inject(ModalService);
  dialogService = inject(DialogService);
  confirmationService = inject(ConfirmationService);

  private _isLoading = signal<boolean>(false);
  public isLoading = computed(() => this._isLoading());

  cols!: Column[];
  sections!: Section[];
  utilsService = inject(UtilsService);

  constructor() {
    this.route.data.subscribe(data => {
      this.title = data['title'];
      this.type = data['type'];
    });
  }

  ngOnInit(): void {
    this.utilsService
      .getSectionTypes()
      .pipe(
        map(sectionTypes =>
          sectionTypes.find(sectionType => sectionType.id === this.type)
        )
      )
      .subscribe(sectionType => {
        this.selectedSectionType = sectionType;
        this.cols = [
          { field: 'active', header: 'Activo' },
          { field: 'id', header: 'Id' },
          { field: 'image', header: 'Imagen' },
          {
            field: 'title',
            header: this.selectedSectionType?.title_label || 'Titulo',
          },
          {
            field: 'description',
            header:
              this.selectedSectionType?.description_label || 'Descripción',
          },
        ];
      });
    this._isLoading.set(true);
    this.utilsService
      .getLanguages()
      .pipe(
        tap(languages => {
          this.languages = languages;
          this.selectedCountry = languages[0];
        }),
        mergeMap(() =>
          this.sectionService
            .getSections(this.type, this.selectedCountry.lang)
            .pipe(finalize(() => this._isLoading.set(false)))
        )
      )
      .subscribe((response: Section[]) => (this.sections = response));
  }

  getSectionsList(lang: string, showLoading: boolean) {
    this._isLoading.set(showLoading);
    this.sectionService
      .getSections(this.type, lang)
      .pipe(finalize(() => this._isLoading.set(false)))
      .subscribe((response: Section[]) => (this.sections = response));
  }

  getSectionByLang(lang: string) {
    return this.sections?.filter((s: Section) => s.lang === lang);
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  exportToExcel(table: Table) {
    table.exportCSV();
  }

  onRowReorder(event: TableRowReorderEvent) {
    if (
      Object.keys(this.dt.filters).length === 0 &&
      event.dragIndex !== undefined &&
      event.dropIndex !== undefined &&
      event.dragIndex !== event.dropIndex
    ) {
      const reorderedSections = this.sections.map((section, index) => {
        return { id: section.id, order: index };
      });
      this.sectionService
        .reorder(this.type, reorderedSections)
        .subscribe(() => this.modalService.showSuccessMessage(''));
    }
  }

  changeLang(event: DropdownChangeEvent) {
    this.getSectionsList(event.value.lang, true);
  }

  editSection(sectionId: string | null) {
    const ref = this.dialogService.open(SectionEditComponent, {
      dismissableMask: false,
      header: sectionId === null ? 'Nuevo' : 'Modificar',
      data: {
        sectionId,
        type: this.selectedSectionType,
        title: this.title,
        lang: sectionId ? this.selectedCountry.lang : this.languages[0].lang,
      },
      width: '750px',
      height: '755px',
    });
    ref.onClose.subscribe((reload: boolean) => {
      reload && this.getSectionsList(this.selectedCountry.lang, false);
    });
  }

  deleteSection(id: number) {
    this.confirmationService.confirm({
      message: `Seguro que quiere eliminar?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.sectionService.deleteSection(this.type, id).subscribe(deleted => {
          if (deleted) {
            this.modalService.showSuccessMessage(`Elemento eliminado`);
            this.getSectionsList(this.selectedCountry.lang, false);
          }
        });
      },
    });
  }

  changeActive(r: Section) {
    this.sectionService.activeSection(this.type, r.id).subscribe({
      next: (changed: boolean) => {
        if (changed) {
          this.modalService.showSuccessMessage(`Elemento actualizado`);
          // this.getSectionsList(this.selectedCountry.lang, false);
        }
      },
      error: () => {
        const section = this.sections.find(s => s.id === r.id);
        section && (section.active = !section?.active);
      },
    });
  }

  identify(index: number, item: Section) {
    return item.id;
  }
}
